import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Eye, Trash2, Edit2, LayoutList, RefreshCw, Loader2, BookmarkPlusIcon, Code } from 'lucide-react';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { generateSubmissionScript, generateWelcomeScreenHtml, fetchEnrichmentData, WelcomeScreenConfig } from './SignupFormEditor';
import { signupFormsApi, SignupFormRecord, formConfigsApi, newslettersApi, extractLookupId, formTemplatesLibraryApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { auditService } from '../services/auditService';
import { toast } from 'sonner';
export function SignupForms() {
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [forms, setForms] = useState<SignupFormRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewForm, setPreviewForm] = useState<SignupFormRecord | null>(null);
  const [previewEnrichment, setPreviewEnrichment] = useState<any>(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [savingAsTemplate, setSavingAsTemplate] = useState<string | null>(null);
  const fetchForms = async () => {
    setLoading(true);
    try {
      const data = await signupFormsApi.list();
      setForms(data);
    } catch (err) {
      console.error('Failed to fetch signup forms:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchForms();
  }, []);
  const handleSaveAsTemplate = async (formId: string) => {
    setSavingAsTemplate(formId);
    try {
      const form = await signupFormsApi.get(formId);
      if (!form) {
        toast.error('Could not load form data');
        return;
      }
      await formTemplatesLibraryApi.create({
        Template_Name: (form.Form_Name || 'Untitled') + ' (Template)',
        HTML: form.HTML || '',
        CSS: form.CSS || '',
        JavaScript1: form.JavaScript1 || '',
        Builder_Schema: form.Builder_Schema || '',
        Builder_Mode: form.Builder_Mode || 'code',
        Team: team,
        Region: form.Region || currentSubRegion || ''
      });
      toast.success('Saved as form template', {
        description: `"${form.Form_Name}" saved to the template library.`,
        action: {
          label: 'View Templates',
          onClick: () => navigate('/form-templates')
        },
        duration: 5000
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save as template', {
        description: msg
      });
    } finally {
      setSavingAsTemplate(null);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      let linkedConfigCount = 0;
      let linkedNewsletterCount = 0;
      try {
        const linkedConfigs = await formConfigsApi.findByFormId(id);
        for (const config of linkedConfigs) {
          await formConfigsApi.delete(config.ID);
          linkedConfigCount++;
        }
      } catch (cleanupErr) {
        console.warn('Failed to clean up linked configurations:', cleanupErr);
      }
      try {
        const allNewsletters = await newslettersApi.list();
        const linkedNewsletters = allNewsletters.filter(nl => {
          const nlFormId = extractLookupId(nl.Sign_Up_Form);
          return nlFormId === id;
        });
        linkedNewsletterCount = linkedNewsletters.length;
      } catch (nlErr) {
        console.warn('Failed to check linked newsletters:', nlErr);
      }
      await signupFormsApi.delete(id);
      setForms(prev => prev.filter(f => f.ID !== id));
      setDeleteConfirm(null);
      // Fire-and-forget: Audit log on delete
      const deletedForm = forms.find(f => f.ID === id);
      auditService.log({
        actionType: 'deleted',
        entityType: 'form',
        entityId: id,
        entityName: deletedForm?.Form_Name || id,
        userName: currentUser.User_Name,
        userEmail: currentUser.Email,
        userRole: currentUser.Role,
        team: currentWorkspace.name,
        region: currentSubRegion || ''
      });
      if (linkedConfigCount > 0 || linkedNewsletterCount > 0) {
        const parts: string[] = [];
        if (linkedConfigCount > 0) parts.push(`${linkedConfigCount} configuration(s) removed`);
        if (linkedNewsletterCount > 0) parts.push(`${linkedNewsletterCount} newsletter(s) still reference this form`);
        toast.success('Signup form deleted', {
          description: parts.join('. '),
          duration: linkedNewsletterCount > 0 ? 8000 : 4000
        });
      } else {
        toast.success('Signup form deleted');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to delete form', {
        description: msg
      });
    } finally {
      setIsDeleting(false);
    }
  };
  const getStatusBadge = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-6314" data-mp="1ha7hx1dmj9gr">
          Active
        </span>;
    if (s === 'draft') return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-6315" data-mp="17b8blpgbzpwx">
          Draft
        </span>;
    if (s === 'archived') return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-6316" data-mp="31qauv5esebre">
          Archived
        </span>;
    return <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600" data-id="element-6317" data-mp="2xm312tth25mb">
        {status || '—'}
      </span>;
  };
  // Filter by team and region
  const filteredForms = forms.filter(f => {
    if (f.Team !== currentWorkspace.name && f.Team !== team) return false;
    if (currentSubRegion) {
      return !f.Region || f.Region === currentSubRegion;
    }
    return true;
  });
  return <div className="space-y-6" data-id="element-6318" data-mp="1ds9sax1b91rz">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-6319" data-mp="1dt2y9vsg3n92">
        <div className="min-w-0" data-id="element-6320" data-mp="1iq1tkhnui08u">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6321" data-mp="1iqbjk58w47ej">Signup Forms</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6322" data-mp="14qk37xuwiexd">
            Manage your custom HTML signup forms stored in Zoho Creator.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-6323" data-mp="3phu9hrmmh3jp">
          <button onClick={fetchForms} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-6324" data-mp="3pg9gw7jvbuoh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6325" data-mp="3hytclp6bu85b" />
          </button>
          <Link to="/forms/new" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap" data-id="element-6326" data-mp="3vycgu6w0ozcc">
            <Plus className="h-4 w-4" data-id="element-6327" data-mp="33a6zlu469sh9" />
            <span data-id="element-6328" data-mp="3372n89gjzqfi">Create Form</span>
          </Link>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-6329" data-mp="2ybnbpo60q6m9">
        {loading && forms.length === 0 ? <div className="p-12 text-center" data-id="element-6330" data-mp="3d0rup7kn0evf">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-6331" data-mp="387ysorgjzkn1" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-6332" data-mp="2kie6lzp3s79j">
              Loading signup forms...
            </p>
          </div> : filteredForms.length === 0 ? <div className="p-12 text-center" data-id="element-6333" data-mp="2fhgnnkw9r96z">
            <LayoutList className="mx-auto h-12 w-12 text-gray-400" data-id="element-6334" data-mp="1u11es33cksi9" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-6335" data-mp="1yzco8cpqd0zy">
              No forms found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-6336" data-mp="23xwbcgg28itw">
              Create your first signup form to get started.
            </p>
            <Link to="/forms/new" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-6337" data-mp="28ya9n6rv0eeq">
              <Plus className="h-4 w-4" data-id="element-6338" data-mp="2hwm8fw2vqii9" />
              Create First Form
            </Link>
          </div> : <div className="overflow-x-auto" data-id="element-6339" data-mp="2mr07c8gr37ro">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-6340" data-mp="2rojobg4u1ynb">
              <thead className="bg-gray-50" data-id="element-6341" data-mp="2rpurpeud94dw">
                <tr data-id="element-6342" data-mp="2wo4jdfgvtmur">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6343" data-mp="2woo0itypk77q">
                    Form Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6344" data-mp="p1l8e16mde3c">
                    Slug
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6345" data-mp="l240l13q04v0">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6346" data-mp="b34u9p6fglb6">
                    Team
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6347" data-mp="13ppm0qqtezf9">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-6348" data-mp="u17m0bqd0wco">
                {filteredForms.map(form => <tr key={form.ID} className="hover:bg-gray-50" data-id="element-6349" data-mp="1rglhpyye6ers">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6350" data-mp="1moldhubagzpe">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" data-id="element-6351" data-mp="1mn0labcg5sup">
                        {form.Form_Name || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6352" data-mp="tapls2nez2cd">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600" data-id="element-6353" data-mp="186n8yvfd0wcg">
                        {form.Url_Slug || '—'}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6354" data-mp="1h5ijuktcmum3">
                      {getStatusBadge(form.Status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-6355" data-mp="1c5mo18ut29hm">
                      {form.Team || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6356" data-mp="1lybnf5d2zwwl">
                      <div className="flex items-center justify-end gap-2" data-id="element-6357" data-mp="1zy379mqa47ii">
                        {form.HTML && <button onClick={async () => {
                    setPreviewForm(form);
                    setPreviewEnrichment(null);
                    setEnrichmentLoading(true);
                    try {
                      const enrichment = await fetchEnrichmentData(form.ID);
                      console.log('[SignupForms] Enrichment loaded for preview:', {
                        formId: form.ID,
                        formName: form.Form_Name,
                        hasEnrichment: !!enrichment,
                        emailSubject: enrichment?.emailSubject,
                        hasEmailHtml: !!enrichment?.emailHtml,
                        rulesCount: enrichment?.rules?.length
                      });
                      setPreviewEnrichment(enrichment);
                    } catch (err) {
                      console.error('Failed to load enrichment for preview:', err);
                    } finally {
                      setEnrichmentLoading(false);
                    }
                  }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Preview" data-id="element-6358" data-mp="1vx14wx12ffcb">
                            <Eye className="h-4 w-4" data-id="element-6359" data-mp="3tio1xmstcwa5" />
                          </button>}
                        <Link to={`/forms/${form.ID}`} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit" data-id="element-6360" data-mp="2i1ehoy1v207s">
                          <Edit2 className="h-4 w-4" data-id="element-6361" data-mp="2307ohp3yo1m9" />
                        </Link>
                        <Link to={`/forms/${form.ID}/embed`} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Embed Code" data-id="element-6362" data-mp="27ysobnfawja6">
                          <Code className="h-4 w-4" data-id="element-6363" data-mp="2lpe95hobf3ap" />
                        </Link>
                        <button onClick={() => handleSaveAsTemplate(form.ID)} disabled={savingAsTemplate === form.ID} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded disabled:opacity-50" title="Save as Template" data-id="element-6364" data-mp="2lrif055rycdm">
                          {savingAsTemplate === form.ID ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-6365" data-mp="167lzf5ixzd9b" /> : <BookmarkPlusIcon className="h-4 w-4" data-id="element-6366" data-mp="2wog3c4ga9wvv" />}
                        </button>
                        <button onClick={() => setDeleteConfirm(form.ID)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-6367" data-mp="2rpwiy8hc8t5m">
                          <Trash2 className="h-4 w-4" data-id="element-6368" data-mp="2d0qfu9fdqisg" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Preview Modal */}
      <PreviewModal isOpen={!!previewForm} onClose={() => {
      setPreviewForm(null);
      setPreviewEnrichment(null);
    }} title={`Preview: ${previewForm?.Form_Name}`} data-id="element-6369" data-mp="2i035f1xmy3yq">
        {previewForm && enrichmentLoading && <div className="flex items-center justify-center py-20" data-id="element-6370" data-mp="1p9uspplvcbrj">
            <div className="text-center" data-id="element-6371" data-mp="4t3kr976a6kr">
              <Loader2 className="h-6 w-6 text-blue-500 animate-spin mx-auto mb-3" data-id="element-6372" data-mp="9kli7n9zlp0z" />
              <p className="text-sm text-gray-500" data-id="element-6373" data-mp="9mptwre4nsbt">
                Loading email template data…
              </p>
            </div>
          </div>}
        {previewForm && !enrichmentLoading && <>
            {!previewEnrichment && <div className="mx-4 mt-2 mb-0 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-id="element-6374" data-mp="oixdbphpopq1">
                <svg className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-6375" data-mp="sh4w0pvqk13v">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" data-id="element-6376" data-mp="2z95qdw6aahs1" />
                </svg>
                <div data-id="element-6377" data-mp="2g5u3johxrmjv">
                  <p className="font-medium" data-id="element-6378" data-mp="2g7ewxasikzg5">No email template linked</p>
                  <p className="mt-0.5 text-amber-700" data-id="element-6379" data-mp="2bd0yt0mgwe7s">
                    Submissions won't include email HTML/subject. Make sure this
                    form has a Form Configuration with a linked email template.
                  </p>
                  <button className="mt-1.5 text-amber-900 underline font-medium hover:text-amber-700" onClick={async () => {
              setEnrichmentLoading(true);
              try {
                const enrichment = await fetchEnrichmentData(previewForm.ID);
                console.log('[SignupForms] Retry enrichment:', {
                  formId: previewForm.ID,
                  hasEnrichment: !!enrichment,
                  rules: enrichment?.rules?.length
                });
                setPreviewEnrichment(enrichment);
              } catch (err) {
                console.error('Retry enrichment failed:', err);
              } finally {
                setEnrichmentLoading(false);
              }
            }} data-id="element-6380" data-mp="1whb9asi2piyb">
                    Retry loading
                  </button>
                </div>
              </div>}
            <FormPreview key={`preview-${previewForm.ID}-${previewEnrichment ? 'enriched' : 'plain'}`} html={(() => {
          let settings: any = {};
          try {
            if (previewForm.Builder_Schema) settings = JSON.parse(previewForm.Builder_Schema);
          } catch {}
          // Wrap with welcome screen if enabled
          if (settings.welcomeScreen?.enabled) {
            const welcomeConfig: WelcomeScreenConfig = {
              enabled: true,
              title: settings.welcomeScreen.title || '',
              description: settings.welcomeScreen.description || '',
              buttonText: settings.welcomeScreen.buttonText || 'Join Us',
              imageUrl: settings.welcomeScreen.imageUrl || '',
              estimatedTime: settings.welcomeScreen.estimatedTime || '',
              backgroundColor: settings.welcomeScreen.backgroundColor || '#000000',
              textColor: settings.welcomeScreen.textColor || '#ffffff',
              editorMode: settings.welcomeScreen.editorMode,
              customHtml: settings.welcomeScreen.customHtml,
              customCss: settings.welcomeScreen.customCss
            };
            const containerId = 'list-preview-' + (previewForm.Url_Slug || previewForm.ID);
            return generateWelcomeScreenHtml(welcomeConfig, previewForm.HTML, containerId);
          }
          return previewForm.HTML;
        })()} css={previewForm.CSS} js={previewForm.JavaScript1} submissionScript={(() => {
          let settings: any = {};
          try {
            if (previewForm.Builder_Schema) settings = JSON.parse(previewForm.Builder_Schema);
          } catch {}
          return generateSubmissionScript(team, previewForm.Url_Slug || '', previewEnrichment, settings.successMessage, settings.submittingText, settings.errorMessage, settings.validationErrorMessage, settings.postSubmitRedirectUrl, settings.rules);
        })()} data-id="element-6381" data-mp="3bgi90srml6bq" />
          </>}
      </PreviewModal>

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6382" data-mp="19l3r30obmuxm">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6383" data-mp="vt5oz9t11zay">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-6384" data-mp="zso41oy63g5v" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-6385" data-mp="1nh7f2c8lq71n">
              <div className="flex items-center gap-4" data-id="element-6386" data-mp="1sg0tq7zmk43e">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-6387" data-mp="1ej59rkhu4s8k">
                  <Trash2 className="h-6 w-6 text-red-600" data-id="element-6388" data-mp="1jg3x9k6zu0xs" />
                </div>
                <div data-id="element-6389" data-mp="32x9jppvlt02">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-6390" data-mp="31lyd4u3piyx">
                    Delete Signup Form
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-6391" data-mp="7zx8zhs7zxi7">
                    Are you sure? This will permanently remove the form from
                    Zoho Creator.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-6392" data-mp="9jp3rjp0rfu6">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-6393" data-mp="9h2xrohs18e2">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-6394" data-mp="3mpp8hx9ya9sy">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-6395" data-mp="2pbcyr9kh2yac" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}